import { EmployeeRole } from "@1games-ru/region-shared/dist/enums/business/employee";
import { BusinessRole } from "@1games-ru/region-shared/dist/enums/business/role";
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import formatPrice from "@/core/helpers/formatPrice";
import useAppSelector from "@/core/helpers/useAppSelector";
import { Button, Checkbox, Icon, Location, Modal, Select } from "@/shared/ui";
import Loader from "@/shared/ui/Loader";
import OfferPlayerInfo from "@/shared/widgets/OfferPlayerInfo";
import OfferView from "@/shared/widgets/OfferView";
import { offerCreatorSelector } from "@/domains/offer/slice";
import getBusinessImage from "@/domains/business/helpers/getBusinessImage";
import useOfferCreatorEmployee from "@/domains/offer/hooks/useOfferCreatorEmployee";

export default function OfferEstateBusinessEmployeeCreatorContainer({
  isShow,
}: {
  isShow: boolean;
}) {
  const { t } = useTranslation("offer");

  const [isAgree, setIsAgree] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectIndex, setSelectIndex] = useState(0);

  const offerCreator = useAppSelector(offerCreatorSelector);

  const { businesses, create, close, pending, loading } = useOfferCreatorEmployee();

  const role = businesses?.length > 0 ? businesses?.[activeIndex]?.role : null;

  const positionsList = useMemo(() => {
    if (role === BusinessRole.Manager) {
      return [
        {
          id: 0,
          role: EmployeeRole.Worker,
          text: t("business:business.employees.position.serviceStaff"),
        },
      ];
    }
    return [
      {
        id: 0,
        role: EmployeeRole.Worker,
        text: t("business:business.employees.position.serviceStaff"),
      },
      {
        id: 1,
        role: EmployeeRole.Manager,
        text: t("business:business.employees.position.manager"),
      },
    ];
  }, [t, role]);

  const businessList = useMemo(() => {
    if (businesses?.length > 0) {
      return businesses.filter((item) => item.role !== BusinessRole.Worker);
    }
    return [];
  }, [businesses]);

  const businessId = businessList[activeIndex]?.id;
  const notEmpty = !pending && !loading && businessList?.length > 0;

  const filteredPositionsList = useMemo(() => {
    return positionsList.filter((item) => item.id !== positionsList[selectIndex]?.id);
  }, [selectIndex, positionsList]);

  const address = businessList
    ? `${businessList[activeIndex]?.address?.streetName} ${businessList[activeIndex]?.address?.index}`
    : "";

  const onSubmit = useCallback(() => {
    return create(businessId, offerCreator?.targetCharacter?.id, positionsList[selectIndex].role);
  }, [businessId, create, offerCreator?.targetCharacter?.id, positionsList, selectIndex]);

  const reset = useCallback(() => {
    setIsAgree(false);
    setActiveIndex(0);
    setSelectIndex(0);
  }, []);

  const toggleAgree = useCallback(() => setIsAgree((state) => !state), []);

  const footer = (
    <Button
      className={clsx("h-11 mr-1", !notEmpty && "!opacity-0 pointer-events-none")}
      onClick={onSubmit}
      disabled={!isAgree}
      loading={pending || loading}
    >
      {t("offer.accept")}
    </Button>
  );

  const leftText = (
    <div className="flex items-center gap-2.5">
      <Icon category="business" name="briefcase-2" className="size-4" />
      <p className="text-15 font-bold leading-none">
        {businessList[activeIndex]?.name} №{businessId}
      </p>
    </div>
  );

  const rightText = (
    <div className="flex flex-col gap-0.5 items-end ml-auto">
      <p className="text-14 font-bold">{formatPrice(businessList[activeIndex]?.baseFee)} ₽</p>
      <p className="text-11 font-medium uppercase">{t("business:business.feePerDay")}</p>
    </div>
  );

  useEffect(() => {
    if (!isShow) {
      reset();
    }
  }, [isShow, reset]);

  useLayoutEffect(() => {
    if (role === BusinessRole.Manager) {
      setActiveIndex(0);
    }
  }, [role]);

  useLayoutEffect(() => {
    if (!positionsList.some((item) => item.id === selectIndex)) {
      setSelectIndex(0);
    }
  }, [positionsList, selectIndex]);

  return (
    <Modal
      open={isShow}
      title={t("offer.business.creator.title")}
      onClose={close}
      footer={footer}
      className="w-[30rem] h-[45rem] pt-6.5 pb-7.5 pl-7.5 pr-6.5"
      classNameCloseBtn="top-6.5 right-7.5 z-[4]"
      style={{ fontFamily: "TT Norms Pro", boxShadow: "0px 0px 200px 0px rgba(0, 121, 194, 0.30)" }}
      dataMzAccent="lightBlue"
    >
      <div className="flex flex-col gap-4">
        {!notEmpty || pending || loading ? (
          <div className="absolute top-0 left-0 z-3 flex justify-center items-center size-full text-16 font-bold text-white/50 text-center leading-none uppercase">
            {!pending && !loading && t("offer.empty")}
            {(pending || loading) && <Loader />}
          </div>
        ) : (
          <>
            {offerCreator && offerCreator.targetCharacter && (
              <OfferPlayerInfo
                name={offerCreator.targetCharacter.name}
                avatar={offerCreator.targetCharacter.avatar}
                id={offerCreator.targetCharacter.id}
                text={t("offer.player")}
              />
            )}

            <div>
              <p className="mb-3.5 text-16 font-bold leading-none">
                {t("offer.business.creator.business")}:
              </p>
              <OfferView
                src={getBusinessImage(businessId)}
                level={t(`business:business.level.${businessList[activeIndex]?.level}`)}
                list={businessList || []}
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                leftText={leftText}
                rightText={rightText}
              />
            </div>

            <Location address={address}>
              <div
                className="relative flex w-[6.6875rem] h-6 ml-auto"
                style={{
                  backgroundImage: `url(${getBusinessImage(businessId, "logo")})`,
                  backgroundPosition: "top right",
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "contain",
                }}
              />
            </Location>

            <div className="flex items-center gap-2.5 px-1 pb-4 border-solid border-b border-b-[#191919]">
              <Icon name="id" className="size-5 opacity-60" />
              <div className="flex gap-1 text-16 font-medium leading-none">
                <p className="text-white/60">{t("business:business.id")}:</p>
                <p>#{businessId}</p>
              </div>
            </div>

            <div>
              <p className="mb-3 text-11 font-bold text-white/50 leading-none uppercase">
                {t("business:business.selectPosition")}
              </p>
              <Select value={<p>{positionsList?.[selectIndex]?.text}</p>}>
                <div className="flex flex-col">
                  {filteredPositionsList.map((item) => (
                    <button
                      key={item.id}
                      className="relative z-1 py-2.5 px-4 text-left bg-dark-800 rounded-b-xl"
                      onClick={() => setSelectIndex(item.id)}
                      type="button"
                    >
                      {item.text}
                    </button>
                  ))}
                </div>
              </Select>
            </div>

            <div className="flex items-center gap-2.5">
              <Checkbox checked={isAgree} onChange={toggleAgree} />
              <button
                type="button"
                onClick={toggleAgree}
                className="text-14 font-medium text-inverted"
              >
                {t("offer.business.creator.agree")}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
